import Select, { SelectInstance, components, DropdownIndicatorProps, OptionProps, GroupBase } from 'react-select';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FaAngleDown, FaChevronUp } from "react-icons/fa6";
import { useIsMobile } from '../../hooks/useIsMobile';

type Option = {
    text: string;
    value: string | number | { value: string; id: string | number };
};

interface DropdownProps {
    options: Option[];
    selected;
    onChangeEvent;
    isMultiSelect?: boolean;
    disabled?: boolean;
    customStyles?: string;
    closeMenuOnSelect?: boolean;
    selectRef?: React.RefObject<SelectInstance<Option, boolean>>;
    dataTestId?: string;
    ariaLabel?: string;
}

// Mobile Multi-Select Component
function MobileMultiSelect({ options, selected, onChangeEvent, disabled, dataTestId, ariaLabel }: {
    options: Option[];
    selected: string;
    onChangeEvent: (options: Option[]) => void;
    disabled: boolean;
    dataTestId?: string;
    ariaLabel?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const selectedValues = selected ? selected.split(',').filter(Boolean) : [];

    const toggleOption = (value: string) => {
        if (disabled) return;

        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];

        // Call onChangeEvent the same way react-select does
        // It expects an array of Option objects, not just values
        const selectedOptions = newSelected.map(val =>
            options.find(opt => String(opt.value) === val)
        ).filter(Boolean);

        onChangeEvent(selectedOptions);

        // Auto-close dropdown when 10 selections are reached
        if (newSelected.length >= 10) {
            setIsOpen(false);
        }
    };

    const clearAllSelections = () => {
        if (disabled) return;
        onChangeEvent([]);
    };

    // Filter out already selected options
    const availableOptions = options.filter(option =>
        !selectedValues.includes(String(option.value))
    );

    return (
        <div className="relative" data-testid={dataTestId}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={ariaLabel || 'Select options'}
                className={`w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-1000 text-left flex justify-between items-center ${disabled ? 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed' : 'cursor-pointer'
                    }`}
            >
                <div className="flex-1">
                    {selectedValues.length === 0 ? (
                        <span className="text-gray-400">Select options...</span>
                    ) : (
                        <div className="flex flex-wrap gap-1">
                            {selectedValues
                                .map(val => options.find(opt => String(opt.value) === val)?.text)
                                .filter(Boolean)
                                .map((text, index) => (
                                    <span
                                        key={index}
                                        className="bg-orange-1000 text-white px-2 py-1 rounded text-sm"
                                    >
                                        {text}
                                    </span>
                                ))
                            }
                        </div>
                    )}
                </div>

                <div className="flex items-center ml-2">
                    {/* Clear selections button - only show when there are selections */}
                    {selectedValues.length > 0 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                clearAllSelections();
                            }}
                            aria-label="Clear all selections"
                            className="flex items-center justify-center hover:bg-gray-100 text-gray-600 font-bold"
                            style={{ width: '20px', height: '20px', fontSize: '14px' }}
                            disabled={disabled}
                        >
                            ×
                        </button>
                    )}

                    {/* Pipe separator - only show when there are selections */}
                    {selectedValues.length > 0 && (
                        <div className="w-px h-4 bg-gray-300 mx-1"></div>
                    )}

                    {/* Dropdown toggle icon - always show */}
                    <div className="flex items-center justify-center" style={{ width: '20px', height: '20px' }}>
                        {isOpen ? (
                            <FaChevronUp className="w-4 h-4 text-gray-600" />
                        ) : (
                            <FaAngleDown className="w-4 h-4 text-gray-600" />
                        )}
                    </div>
                </div>
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        aria-hidden="true"
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        role="listbox"
                        aria-label={ariaLabel || 'Options'}
                        className="absolute top-full left-0 right-0 z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded mt-1 shadow-lg"
                        style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch' // Enable smooth scrolling on iOS
                        }}
                    >
                        {availableOptions?.length > 0 ? (
                            availableOptions.map((option) => (
                                <div
                                    key={String(option.value)}
                                    role="option"
                                    aria-selected={selectedValues.includes(String(option.value))}
                                    tabIndex={0}
                                    data-testid="option"
                                    onTouchStart={(e) => e.stopPropagation()} // Prevent touch conflicts
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleOption(String(option.value));
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleOption(String(option.value));
                                    }}
                                    className="p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 bg-white dark:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    style={{
                                        minHeight: '48px', // Ensure touch targets are large enough
                                        touchAction: 'manipulation', // Improve touch response
                                        userSelect: 'none' // Prevent text selection
                                    }}
                                >
                                    <span className="text-base">{option.text}</span>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-gray-500 dark:text-gray-400 text-center">
                                {selectedValues.length === options.length
                                    ? 'All options selected'
                                    : 'No options available'
                                }
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default function Dropdown({ options, selected, onChangeEvent, disabled = false, isMultiSelect = false, customStyles = '', closeMenuOnSelect = true, selectRef, dataTestId, ariaLabel }: DropdownProps) {
    const internalSelectRef = useRef<SelectInstance<Option, boolean>>(null);
    const effectiveSelectRef = selectRef || internalSelectRef;
    const isMobile = useIsMobile();

    const selectedOption = isMultiSelect
        // ? options?.filter((option) => selected.includes(option.value))
        ? options?.filter((option) => selected?.split(',')?.find(opt => opt === option.value))
        : options?.find((option) => option.value === selected) || null;

    const CustomOption = useCallback((props: OptionProps<Option, boolean, GroupBase<Option>>) => (
        <components.Option {...props}>
            <div data-testid="option">{props.children}</div>
        </components.Option>
    ), []);

    const CustomDropdownIndicator = useCallback((props: DropdownIndicatorProps<Option, boolean, GroupBase<Option>>) => (
        <components.DropdownIndicator {...props}>
            <svg
                data-testid={dataTestId ? `${dataTestId}-indicator` : undefined}
                viewBox="0 0 20 20"
                aria-hidden="true"
                focusable="false"
                className="w-5 h-5 fill-current"
            >
                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502a1.095 1.095 0 0 1-1.576 0s-4.287-4.084-4.695-4.502c-0.408-0.418-0.436-1.17 0-1.615z" />
            </svg>
        </components.DropdownIndicator>
    ), [dataTestId]);

    const customComponents = useMemo(() => ({
        DropdownIndicator: CustomDropdownIndicator,
        Option: CustomOption
    }), [CustomDropdownIndicator, CustomOption]);

    // Use custom mobile component for mobile multi-select
    if (isMobile && isMultiSelect) {
        return (
            <MobileMultiSelect
                options={options}
                selected={selected}
                onChangeEvent={onChangeEvent}
                disabled={disabled}
                dataTestId={dataTestId}
                ariaLabel={ariaLabel}
            />
        );
    }

    // Use standard react-select for all other cases (desktop or single-select)
    return (
        <div data-testid={dataTestId}>
            <Select
                ref={effectiveSelectRef}
                isDisabled={disabled}
                closeMenuOnSelect={closeMenuOnSelect}
                inputId={dataTestId ? `${dataTestId}-input` : undefined}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: '#fdb419', // Inside select color
                        primary: '#fdb419', // Inside select color
                        neutral0: 'var(--select-background)', // Background color
                        neutral80: 'var(--select-text-color)', // Text color
                        neutral20: 'gray', // Border color
                    },
                })}
                isMulti={isMultiSelect}
                value={selectedOption}
                onChange={onChangeEvent}
                options={options}
                aria-label={ariaLabel}
                components={customComponents}
                classNames={{
                    control: (state) => `${customStyles}  ${state.isDisabled ? '!bg-gray-200 !cursor-not-allowed' : ''}`,
                    singleValue: (state) =>
                        state.isDisabled ? '!text-neutral-500' : '',
                    multiValue: (state) =>
                        state.isDisabled ? '!text-neutral-500 !bg-gray-200' : '!bg-orange-1000 !text-white',
                    multiValueLabel: (state) =>
                        state.isDisabled ? '!text-neutral-500' : '!text-white',
                    multiValueRemove: (state) =>
                        state.isDisabled ? '!text-neutral-500' : '!text-white hover:!bg-orange-900 hover:!text-white',
                    placeholder: (state) =>
                        state.isDisabled ? '!text-neutral-500' : '',
                }}
                getOptionLabel={(option: Option) => option.text}
            />
        </div>
    );
}